import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaDownload,
  FaPrint,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import useAuth from "../../hooks/useAuthContext";
import useApp from "../../hooks/useAppContext";
import api from "../../services/axios";
import { formatarDiaHora } from "../../services/conversaoTimer";
import exportToExcel from "../../services/exportExcel";
import ModalPesquisa from "../../components/modal/modalPesquisa";
import "../adm/admPatrocinadoresList.css";
import "./reuniao.css";

const LABELS_TIPO = {
  reuniao_negocio: "Reunião",
  m_m: "M&M",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
};

const AVALIACOES = [
  "",
  "",
  "Não foi boa",
  "Aproximou relacionamento",
  "Gerou proposta",
  "Vamos fechar negócio",
];

function idUsuarioAp(ap) {
  return Number(ap?.id_usuario ?? ap?.idUsuario);
}

function getOutrosParticipantes(agenda, userId) {
  const idNum = parseInt(userId, 10);
  return (agenda?.participantes || []).filter(
    (ap) => idUsuarioAp(ap) !== idNum
  );
}

function textoAvaliacao(rate) {
  return AVALIACOES[rate] || "—";
}

function montarResultadoConvidado(convidadoAp, patrocinadorAp) {
  if (!convidadoAp) return "Não Avaliado";
  if (convidadoAp.status === 1 && patrocinadorAp?.status === 1) {
    return textoAvaliacao(convidadoAp.rate);
  }
  if (convidadoAp.status === 1) {
    return "Avalie a reunião para visualizar o resultado";
  }
  return "Não Avaliado";
}

function montarResultadoPatrocinador(patrocinadorAp) {
  if (!patrocinadorAp) {
    return { texto: "Não Avaliado", podeResponder: false };
  }
  if (patrocinadorAp.status === 1) {
    return {
      texto: textoAvaliacao(patrocinadorAp.rate),
      podeResponder: false,
    };
  }
  if (patrocinadorAp.presenca) {
    return { texto: null, podeResponder: true };
  }
  return { texto: "Reunião não realizada", podeResponder: false };
}

function montarItensAgenda(reunioes, userId) {
  const itens = [];
  const idNum = parseInt(userId, 10);

  for (const r of reunioes || []) {
    const agenda = r?.agenda;
    if (!agenda) continue;

    if (agenda.tipo === "reuniao_negocio" || agenda.tipo === "m_m") {
      const outros = getOutrosParticipantes(agenda, userId);
      if (!outros.length) continue;

      const convidadoAp = outros[0];
      const convidado = convidadoAp?.usuario;
      const patrocinadorAp =
        (agenda.participantes || []).find(
          (ap) => idUsuarioAp(ap) === idNum
        ) || r;

      const resultadoPatrocinador = montarResultadoPatrocinador(patrocinadorAp);

      itens.push({
        key: r.id,
        idAgenda: agenda.id,
        tipo: agenda.tipo,
        horario: formatarDiaHora(agenda.dt_hora_inicio).substring(0, 16),
        titulo: (convidado?.nome || "—").toUpperCase(),
        cargo: convidado?.cargo || "",
        empresa: convidado?.empresa || "",
        kind: "reuniao",
        isMm: agenda.tipo === "m_m",
        isExtra: agenda.nome === "Reunião de negócios*",
        isAlmoco: agenda.nome === "Reunião de almoço/jantar",
        resultadoConvidado: montarResultadoConvidado(
          convidadoAp,
          patrocinadorAp
        ),
        resultadoPatrocinador: resultadoPatrocinador.texto,
        podeResponderPesquisa: resultadoPatrocinador.podeResponder,
        pesquisa: {
          id: patrocinadorAp.id,
          agenda,
        },
      });
    }
  }

  return itens;
}

/**
 * Fluxo: usuário logado → id_patrocinador → info_patrocinadores.id_usuario
 * Ex.: logado vinculado ao patrocinador 1, cuja coluna id_usuario = 3 → agenda do usuário 3.
 */
async function resolverIdUsuarioPatrocinador(usuario) {
  if (!usuario?.id) return null;

  const idInfoPatrocinador = usuario.id_patrocinador;
  if (!idInfoPatrocinador) {
    throw new Error(
      "Usuário logado não possui patrocinador vinculado (id_patrocinador)."
    );
  }

  const respPatro = await api.get(`/patrocinador/${idInfoPatrocinador}`);
  const info = respPatro.data.dados;
  const idUsuario =
    info?.id_usuario ?? info?.idUsuario ?? info?.usuario?.id ?? null;

  if (!idUsuario) {
    throw new Error(
      "Patrocinador sem usuário principal (id_usuario) em info_patrocinadores."
    );
  }

  const respAgenda = await api.get(`/usuarios-get/${idUsuario}`);
  return {
    idUsuario: Number(idUsuario),
    idInfoPatrocinador: Number(idInfoPatrocinador),
    dados: respAgenda.data.user,
    infoPatrocinador: info,
  };
}

const Reuniao = () => {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const { tipoUsuario } = useApp();

  const [patrocinador, setPatrocinador] = useState(null);
  const [nomePatrocinador, setNomePatrocinador] = useState("");
  const [idUsuarioPatrocinador, setIdUsuarioPatrocinador] = useState(null);
  const [reunioes, setReunioes] = useState([]);
  const [quantidadeMarcada, setQuantidadeMarcada] = useState(0);
  const [showContador, setShowContador] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [showModalPesquisa, setShowModalPesquisa] = useState(false);
  const [pesquisa, setPesquisa] = useState(null);

  const carregarAgenda = useCallback(async () => {
    if (!usuario?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const resolvido = await resolverIdUsuarioPatrocinador(usuario);
      const { idUsuario, dados, infoPatrocinador } = resolvido;

      setPatrocinador(dados);
      setIdUsuarioPatrocinador(idUsuario);
      setNomePatrocinador(
        infoPatrocinador?.patrocinador ||
          dados?.infoPatrocinador?.patrocinador ||
          dados?.nome ||
          ""
      );

      const idInfo =
        dados?.infoPatrocinador?.id ??
        infoPatrocinador?.id ??
        usuario.id_patrocinador;

      if ((dados?.plano || infoPatrocinador?.plano) && idInfo) {
        const respPatrocinador = await api.post(
          "/patrocinador-verifica-disponibilidade",
          { id_info: idInfo }
        );

        const patro = respPatrocinador.data.patrocinador;
        const arrayIds = [];

        (patro.usuarios || []).forEach((u) => {
          (u.agendaParticipante || []).forEach((ap) => {
            if (
              (ap.agenda?.tipo === "reuniao_negocio" ||
                ap.agenda?.tipo === "m_m") &&
              ap.agenda.nome !== "Reunião de almoço/jantar" &&
              ap.agenda.nome !== "Reunião de negócios*"
            ) {
              arrayIds.push(ap.id_agenda);
            }
          });
        });

        setQuantidadeMarcada([...new Set(arrayIds)].length);
        setShowContador(true);
      } else {
        setShowContador(false);
        setQuantidadeMarcada(0);
      }

      const ordenacao = [...(dados.agendaParticipante || [])].sort((a, b) => {
        const dataA = new Date(a.agenda.dt_hora_inicio);
        const dataB = new Date(b.agenda.dt_hora_inicio);
        return dataA - dataB;
      });

      setReunioes(ordenacao);
    } catch (err) {
      console.error("Erro ao carregar reuniões do patrocinador:", err);
      setError(
        err?.message ||
          "Erro ao carregar as reuniões do patrocinador vinculado à sua conta"
      );
      setPatrocinador(null);
      setReunioes([]);
      setIdUsuarioPatrocinador(null);
      setNomePatrocinador("");
    } finally {
      setIsLoading(false);
    }
  }, [usuario]);

  useEffect(() => {
    carregarAgenda();
  }, [carregarAgenda]);

  const itensAgenda = useMemo(
    () => montarItensAgenda(reunioes, idUsuarioPatrocinador),
    [reunioes, idUsuarioPatrocinador]
  );

  const itensFiltrados = useMemo(() => {
    if (!filtro.trim()) return itensAgenda;

    const termo = filtro.toLowerCase().trim();
    return itensAgenda.filter((item) => {
      const campos = [
        item.horario,
        item.titulo,
        item.cargo,
        item.empresa,
        item.tipo,
        LABELS_TIPO[item.tipo],
        item.resultadoConvidado,
        item.resultadoPatrocinador,
        item.podeResponderPesquisa ? "responder pesquisa pendente" : "",
        item.isMm ? "m&m match meet" : "",
        item.isExtra ? "reunião extra" : "",
        item.isAlmoco ? "almoço jantar" : "",
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [itensAgenda, filtro]);

  const indicadores = useMemo(
    () => ({
      total: itensAgenda.length,
      reunioesCount: itensAgenda.filter((i) => i.kind === "reuniao").length,
      cotaReunioes: patrocinador?.plano?.cota_reunioes ?? 0,
      cotaSimultaneas: patrocinador?.plano?.cota_simultania ?? 0,
      quantidadeMarcada,
    }),
    [itensAgenda, patrocinador, quantidadeMarcada]
  );

  const handleDownload = async () => {
    const jsonRelatorio = itensAgenda.map((item) => ({
      Horário: item.horario,
      Convidado: item.titulo,
      Cargo: item.cargo,
      Empresa: item.empresa,
      Tipo: LABELS_TIPO[item.tipo] || item.tipo,
      "Resultado convidado": item.resultadoConvidado,
      "Resultado patrocinador": item.podeResponderPesquisa
        ? "Pendente"
        : item.resultadoPatrocinador,
    }));
    await exportToExcel(jsonRelatorio, "Reuniões do patrocinador");
  };

  const handleModalPesquisa = (item = null) => {
    if (item?.pesquisa) {
      setPesquisa(item.pesquisa);
      setShowModalPesquisa(true);
      return;
    }

    setShowModalPesquisa(false);
    setPesquisa(null);
    carregarAgenda();
  };

  return (
    <div className="main">
      <ModalPesquisa
        titulo="Pesquisa"
        show={showModalPesquisa}
        toggleModal={handleModalPesquisa}
        pesquisa={pesquisa}
        tipoUsuario={tipoUsuario === "participante_patrocinador" ? "patrocinador" : tipoUsuario}
        close={true}
      />

      <div className="corpo-pagina reuniao-page">
        <div className="apl-page">
          <header className="apl-header" aria-label="Cabeçalho da listagem">
            <div className="apl-header-text">
              <h1>Reuniões</h1>
              <p>
                {nomePatrocinador
                  ? `Reuniões de negócios agendadas para ${nomePatrocinador}.`
                  : "Visualize as reuniões de negócios agendadas para o patrocinador vinculado à sua conta."}
              </p>
            </div>

            <div className="apl-toolbar apl-toolbar--inline">
              <div className="apl-search">
                <span className="apl-search-icon" aria-hidden>
                  <FaSearch size={14} />
                </span>
                <input
                  type="search"
                  className="apl-search-input"
                  placeholder="Buscar por horário, nome ou tipo…"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  disabled={isLoading}
                  aria-label="Buscar reunião"
                />
              </div>

              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={handleDownload}
                disabled={isLoading || itensAgenda.length === 0}
              >
                <FaDownload size={14} aria-hidden />
                Download
              </button>

              <button
                type="button"
                className="apl-btn apl-btn--secondary"
                onClick={() =>
                  navigate(
                    `/private/adm/convidados/imprimir/${idUsuarioPatrocinador}`
                  )
                }
                disabled={!idUsuarioPatrocinador}
              >
                <FaPrint size={14} aria-hidden />
                Imprimir
              </button>
            </div>
          </header>

          <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
            {error ? (
              <div className="apl-alert" role="alert">
                {error}
              </div>
            ) : null}

            {!isLoading && patrocinador ? (
              <section
                className="apl-metrics"
                aria-label="Indicadores das reuniões"
              >
                {showContador ? (
                  <>
                    <div className="apl-metric apl-metric--total">
                      <span className="apl-metric-value">
                        {indicadores.quantidadeMarcada}
                        <span
                          className="apl-stat-muted"
                          style={{ fontSize: "1rem" }}
                        >
                          {" "}
                          / {indicadores.cotaReunioes}
                        </span>
                      </span>
                      <span className="apl-metric-label">
                        Reuniões agendadas
                      </span>
                    </div>
                    <div className="apl-metric">
                      <span className="apl-metric-value">
                        {indicadores.cotaSimultaneas}
                      </span>
                      <span className="apl-metric-label">Simultâneas</span>
                    </div>
                    <div className="apl-metric">
                      <span className="apl-metric-value">
                        {indicadores.total}
                      </span>
                      <span className="apl-metric-label">
                        {indicadores.total === 1
                          ? "Item na agenda"
                          : "Itens na agenda"}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="apl-metric apl-metric--total">
                      <span className="apl-metric-value">
                        {indicadores.total}
                      </span>
                      <span className="apl-metric-label">
                        {indicadores.total === 1 ? "Reunião" : "Reuniões"}
                      </span>
                    </div>
                    <div className="apl-metric">
                      <span className="apl-metric-value">
                        {indicadores.reunioesCount}
                      </span>
                      <span className="apl-metric-label">Na listagem</span>
                    </div>
                  </>
                )}
              </section>
            ) : null}

            {isLoading ? (
              <div className="apl-card">
                <div className="apl-loading">
                  <FaSpinner className="fa-spin" size={28} aria-hidden />
                  <span>Carregando reuniões…</span>
                </div>
              </div>
            ) : (
              <section className="apl-card" aria-label="Tabela de reuniões">
                <div className="apl-table-wrap">
                  {itensFiltrados.length === 0 ? (
                    <div className="apl-empty">
                      <p className="apl-empty-title">
                        {itensAgenda.length === 0
                          ? "Nenhuma reunião agendada"
                          : "Nenhum resultado"}
                      </p>
                      <p style={{ margin: 0 }}>
                        {itensAgenda.length === 0
                          ? "Não há reuniões de negócios para este patrocinador."
                          : "Ajuste a busca para ver reuniões."}
                      </p>
                    </div>
                  ) : (
                    <table className="apl-table">
                      <thead>
                        <tr>
                          <th scope="col">Horário</th>
                          <th scope="col">Convidado</th>
                          <th scope="col">Empresa</th>
                          <th scope="col">Tipo</th>
                          <th scope="col">Resultado convidado</th>
                          <th scope="col">Resultado patrocinador</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itensFiltrados.map((item) => (
                          <tr key={item.key}>
                            <td>
                              <span
                                className={`apl-stat${
                                  item.isMm ? " apl-horario--mm" : ""
                                }`}
                              >
                                {item.horario}
                              </span>
                            </td>
                            <td className="apl-cell-name">
                              <div className="apl-cell-stack">
                                <span>{item.titulo}</span>
                                {item.cargo ? (
                                  <span className="apl-stat-muted">
                                    {item.cargo}
                                  </span>
                                ) : null}
                                {item.isExtra ? (
                                  <span className="apl-tag-type apl-tag-type--warn">
                                    Reunião extra
                                  </span>
                                ) : null}
                                {item.isAlmoco ? (
                                  <span className="apl-tag-type apl-tag-type--warn">
                                    Almoço/Jantar
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td>
                              {item.empresa || (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                            <td>
                              {item.tipo ? (
                                <span
                                  className={`apl-tag-type${
                                    item.isMm ? " apl-tag-type--mm" : ""
                                  }`}
                                >
                                  {LABELS_TIPO[item.tipo] || item.tipo}
                                </span>
                              ) : (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className="apl-stat">
                                {item.resultadoConvidado}
                              </span>
                            </td>
                            <td>
                              {item.podeResponderPesquisa ? (
                                <button
                                  type="button"
                                  className="apl-btn apl-btn--primary"
                                  onClick={() => handleModalPesquisa(item)}
                                >
                                  Responder pesquisa
                                </button>
                              ) : (
                                <span className="apl-stat">
                                  {item.resultadoPatrocinador}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {itensFiltrados.length > 0 ? (
                  <footer className="apl-footer">
                    <span>
                      Exibindo <strong>{itensFiltrados.length}</strong>{" "}
                      {itensFiltrados.length === 1 ? "reunião" : "reuniões"}
                      {filtro ? (
                        <>
                          {" "}
                          <span className="apl-stat-muted">
                            (de {itensAgenda.length} no total)
                          </span>
                        </>
                      ) : null}
                    </span>
                    <span>
                      {filtro ? "Busca ativa" : "Agenda do patrocinador"}
                    </span>
                  </footer>
                ) : null}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reuniao;
